import React, { useEffect, useReducer, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import {
    Grid,
    Paper,
    CssBaseline,
    Typography,
    InputAdornment,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import LockIcon from "@material-ui/icons/Lock";
import { makeStyles } from "@material-ui/core/styles";
import loginService from "./loginService";
import DefaultButton from "../../componentes/button/DefaultButton";
import InputField from "../../componentes/form-fields/InputField/InputField";
import DefaultLogo from "../../images/logo.png";
import LoginBackground from "../../images/loginBackground.jpg";
import "./Login.scss";
import { IconLabelSwitch } from "../../componentes/iconSwitch/IconLabelSwitch";
import { persistAuthenticatedUserSession } from "../../configurations/authSession";
import { useTheme } from "../../configurations/themeProvider";

const useStyles = makeStyles((theme) => ({
    icon: {
        color: "#71777B",
        fontSize: 30,
    },
    image: {
        backgroundImage: `url(${LoginBackground})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
    },
    logo: {
        margin: theme.spacing(8, 0, 0, 0),
        maxWidth: 220,
        maxHeight: 80,
        objectFit: "contain",
        [theme.breakpoints.only("xs")]: {
            margin: theme.spacing(8, 0, 4, 0),
        },
    },
    paper: {
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100vh",
        margin: theme.spacing(0, 8, 0, 8),
        maxWidth: 400,
        [theme.breakpoints.only("xs")]: {
            margin: theme.spacing(0, 4, 0, 4),
        },
    },
}));

function reducer(state, action) {
    switch (action.type) {
        case "update":
            return { ...state, ...action.data };
        case "clear":
            return initialUserState;
        case "error":
            return { ...state, error: true };
        default:
            return state;
    }
}

const initialUserState = {
    username: "",
    currentPassword: "",
    error: false,
};

export default function Login() {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const history = useHistory();
    const { control, handleSubmit } = useForm();
    const [user, dispatch] = useReducer(reducer, initialUserState);
    const { username, currentPassword, error } = user;
    const [logoError, setLogoError] = useState(false);
    const { clientTheme, applyAuthenticatedTheme } = useTheme();

    const changeLanguage = (response) => {
        if (response != null && typeof response === "string") {
            i18n.changeLanguage(response);
            sessionStorage.setItem("language", response);
        } else if (response?.target?.value) {
            i18n.changeLanguage(response.target.value);
            sessionStorage.setItem("language", response.target.value);
        } else {
            i18n.changeLanguage("ptBR");
            sessionStorage.setItem("language", "ptBR");
        }
    };

    useEffect(() => {
        loginService.getSystemParameterList().then((response) => {
            sessionStorage.setItem("parameterList", JSON.stringify(response.data));
            response.data.forEach((item) => {
                if (item.parameter === "DEFAULT_LANGUAGE") {
                    changeLanguage(item.textValue);
                }
            });
        });
    }, []);

    useEffect(() => {
        setLogoError(false);
    }, [clientTheme.logoUrl]);

    const handleChange = ({ target: { name, value } }) => {
        dispatch({
            type: "update",
            data: { [name]: value },
        });
    };

    const handleLogin = () => {
        const { error: ignoredError, ...data } = user;
        loginService
            .login(data)
            .then((response) => {
                if (response.status === 200) {
                    debugger;
                    persistAuthenticatedUserSession(response.data);
                    applyAuthenticatedTheme(response.data);

                    if (response.data.firstAccess === 1) {
                        history.push("/change-password");
                    } else {
                        history.push("/home");
                    }
                } else {
                    dispatch({ type: "error" });
                }
            })
            .catch(() => {
                dispatch({ type: "error" });
            });
    };

    const logoSource = !logoError && clientTheme.logoUrl ? clientTheme.logoUrl : DefaultLogo;

    return (
        <Grid container component="main" className={"root"}>
            <CssBaseline />
            <Grid
                item
                xs={12}
                sm={8}
                md={6}
                component={Paper}
                elevation={6}
                square
                className={"paperBackground"}
            >
                <div className={classes.paper}>
                    <img
                        src={logoSource}
                        className={classes.logo}
                        onError={() => setLogoError(true)}
                        alt="Logo do cliente"
                    />
                    <div>
                        <form onSubmit={handleSubmit(handleLogin)}>
                            {error && (
                                <Typography align="center" style={{ color: "red" }}>
                                    {t("loginError")}
                                </Typography>
                            )}
                            <InputField
                                name="username"
                                control={control}
                                defaultValue={""}
                                onChange={handleChange}
                                value={username}
                                length={50}
                                label={t("username")}
                                inputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <AccountCircleIcon className={classes.icon} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <InputField
                                name="currentPassword"
                                control={control}
                                defaultValue={""}
                                onChange={handleChange}
                                value={currentPassword}
                                length={50}
                                label={t("password")}
                                type="password"
                                inputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon className={classes.icon} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <DefaultButton
                                className={"submit"}
                                onClick={handleLogin}
                                content={t("loginButton")}
                                submit
                            />
                        </form>
                    </div>
                    <IconLabelSwitch style={{ margin: "10px" }} name={"copyRight"} labelName={"copyRight"} />
                </div>
            </Grid>
            <Grid item xs={false} sm={4} md={6} className={classes.image} />
        </Grid>
    );
}